/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { compose } from 'recompose';

import Retracked from 'js/app/retracked';

import type { Theme } from '@coursera/cds-core';
import { Grid, typography2 } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import PersonalizedEmbeddedMsg from 'bundles/degree-description/components/modules/personalization/PersonalizedEmbeddedMsg';
import { getCtaActionXddp, getMessagesToDisplay } from 'bundles/degree-description/utils/personalizedMessageCriteria';
import type { AdmissionsContentFragment } from 'bundles/xddp/__generated__/contentfulTypes';
import FinancialSection from 'bundles/xddp/components/FinancialSection';
import AdmissionsPageSchemaMarkup from 'bundles/xddp/components/seo/AdmissionsPageSchemaMarkup';
import Container from 'bundles/xddp/components/shared/Container';
import InfoSidebar from 'bundles/xddp/components/shared/InfoSidebar';
import RichText from 'bundles/xddp/components/shared/RichText';
import { useScrollToStickyBar } from 'bundles/xddp/hooks/useScrollToStickyBar';
import type { DegreeSubPageProps } from 'bundles/xddp/types';

const styles = {
  admissionsBody: (theme: Theme) => css`
    .rc-RichText {
      h2,
      h3,
      h4,
      h5,
      h6,
      p {
        font-family: ${theme.typography.fontFamily};
      }

      h2,
      h3,
      h4,
      h5,
      h6 {
        margin-bottom: var(--cds-spacing-200);
        margin-top: var(--cds-spacing-300);

        & + .faq-item {
          margin-top: var(--cds-spacing-150);
        }
      }

      p {
        margin-bottom: var(--cds-spacing-150);
        ${typography2.bodyPrimary};
      }

      h2 {
        ${theme.typography.h1semibold};
      }

      h3 {
        ${theme.typography.h2semibold};
      }

      h4 {
        ${typography2.subtitleMedium};
      }

      h5 {
        ${typography2.subtitleMedium};
      }

      .faq-item {
        & > div > div {
          border-color: var(--cds-color-grey-50);
        }

        & + p {
          margin-top: var(--cds-spacing-200);
        }

        & + h3 {
          margin-top: var(--cds-spacing-800);
        }

        h3 {
          margin: 0;
        }

        h3 + div *[role='region'] {
          border-top: solid 1px var(--cds-color-grey-50);
          padding-top: var(--cds-spacing-200);
        }
      }
    }
  `,
};

type Props = DegreeSubPageProps & {
  admissionsRequirements: AdmissionsContentFragment;
  showFinancialTab: boolean;
};

const AdmissionsPage = ({
  pageContent,
  admissionsRequirements,
  showFinancialTab,
  userAgent,
  personalizedDdpData,
  handlePersonalizedRequestInfoClick,
  isStickyBarPresent,
}: Props) => {
  const {
    programAudience,
    classProfile,
    atAGlance,
    applicationProcess,
    partnerInformation,
    programName,
    expressionOfInterestContainer,
    slug,
  } = pageContent;

  // Personalization
  const messagesToDisplay = getMessagesToDisplay(
    personalizedDdpData,
    slug,
    programName,
    partnerInformation?.partnerName
  );
  const embeddedAction = getCtaActionXddp(
    messagesToDisplay?.embeddedMessageToDisplay?.ctaActionType,
    expressionOfInterestContainer?.applyButtonUrl ?? expressionOfInterestContainer?.hostedApplicationUrl,
    handlePersonalizedRequestInfoClick
  );

  useScrollToStickyBar(isStickyBarPresent);

  return (
    <div className="rc-AdmissionsPage">
      <AdmissionsPageSchemaMarkup pageContent={pageContent} />
      <Container>
        <Grid container>
          <Grid item md={8} css={styles.admissionsBody}>
            <Markdown className="programAudience rc-Section" source={programAudience} />
            {messagesToDisplay.embeddedMessageToDisplay && (
              <PersonalizedEmbeddedMsg
                personalizedMessage={messagesToDisplay.embeddedMessageToDisplay}
                ctaAction={embeddedAction}
                onXddp
              />
            )}
            {admissionsRequirements?.applicationRequirements ? (
              <RichText
                content={admissionsRequirements?.applicationRequirements?.json}
                links={admissionsRequirements?.applicationRequirements?.links}
              />
            ) : (
              <Markdown className="applicationProcess rc-Section" source={applicationProcess} />
            )}
            <Markdown className="classProfile rc-Section" source={classProfile} />
            <FinancialSection pageContent={pageContent} showFinancialTab={showFinancialTab} />
          </Grid>
          <InfoSidebar userAgent={userAgent} atAGlance={atAGlance} />
        </Grid>
      </Container>
    </div>
  );
};

export default compose<Props, Props>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'xddp',
      page: 'admissions_page',
    },
  }))
)(AdmissionsPage);
